import * as Sentry from '@sentry/react';
import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  ChatMessage,
  ChatMode,
  ChatState,
  FinalChunkData,
  isChatMessage,
  Message,
  MessageContent,
  MessageDirection,
  MessageState,
  PresentedFile,
} from '../types';
import { useChatTransport } from './useChatTransport';

type StateChangeCallback = (state: ChatState, data?: FinalChunkData) => void;

type ClientChatOptions = {
  mode?: typeof ChatMode.CLIENT;
  onStateChange?: StateChangeCallback;
};

type DocumentChatOptions = {
  mode: typeof ChatMode.DOCUMENT;
  entityId: string;
  requestBodyBuilder: (message: string, conversationId: string) => object;
  onStateChange?: StateChangeCallback;
};

type UseChatMessagesOptions = ClientChatOptions | DocumentChatOptions;

type ModeConfig = {
  handledStates: ChatState[];
  fireTransitionCallbacks: boolean;
};

const MODE_CONFIGS: Record<ChatMode, ModeConfig> = {
  [ChatMode.CLIENT]: {
    handledStates: [
      ChatState.THINKING,
      ChatState.ANSWERING,
      ChatState.TOOL_STARTED,
      ChatState.TOOL_FINISHED,
      ChatState.TOOL_LIMIT_REACHED,
      ChatState.FILE_PRESENTED,
    ],
    fireTransitionCallbacks: false,
  },
  [ChatMode.DOCUMENT]: {
    handledStates: [ChatState.THINKING, ChatState.ANSWERING, ChatState.EDITING, ChatState.FINAL],
    fireTransitionCallbacks: true,
  },
};

const createUserMessage = (content: string): ChatMessage => {
  const response = [{ state: 'SENT', content }];
  const { thinkingContent, answerContent, currentState } = computeMessageProperties(response);

  return {
    kind: 'message',
    id: uuidv4(),
    response,
    sender: 'Me',
    direction: MessageDirection.Outgoing,
    thinkingContent,
    answerContent,
    currentState,
  };
};

const createResponseMessage = (content?: string, id?: string): ChatMessage => {
  const response = content ? [{ state: 'ANSWERING', content }] : [];
  const { thinkingContent, answerContent, currentState } = computeMessageProperties(response);

  return {
    kind: 'message',
    id: id || uuidv4(),
    response,
    sender: 'Siesta',
    direction: MessageDirection.Incoming,
    thinkingContent,
    answerContent,
    currentState,
  };
};

const createThinkingPlaceholderMessage = (id: string): ChatMessage => {
  const response: MessageContent[] = [{ state: ChatState.THINKING, content: '' }];
  const { thinkingContent, answerContent, currentState } = computeMessageProperties(response);

  return {
    kind: 'message',
    id,
    response,
    sender: 'Siesta',
    direction: MessageDirection.Incoming,
    thinkingContent,
    answerContent,
    currentState,
  };
};

const findChatMessageById = (messages: Message[], id: string): ChatMessage | undefined =>
  messages.find((msg): msg is ChatMessage => msg.id === id && isChatMessage(msg));

const isEmptyThinkingPlaceholder = (message: Message): boolean =>
  isChatMessage(message) &&
  message.direction === MessageDirection.Incoming &&
  message.response.length === 1 &&
  message.response[0].state === ChatState.THINKING &&
  message.response[0].content === '' &&
  !message.currentToolName &&
  message.currentState !== ChatState.TOOL_LIMIT_REACHED;

type ParsedChunk = {
  state: MessageState;
  content: string;
  data?: FinalChunkData;
};

const parseStreamChunk = (rawData: string): ParsedChunk | null => {
  try {
    const parsed = JSON.parse(rawData);
    return {
      state: parsed.state,
      content: parsed.content,
      data: parsed.data,
    };
  } catch (e) {
    Sentry.captureException(e);
    return null;
  }
};

const processContentTransforms = (content: string): string => {
  const transforms = [
    {
      regex: /<span\s+[^>]*style="[^"]*"[^>]*>(.*?)<\/span>/gi,
      replacement: '$1',
    },

    // Answer content is sometimes incorrectly wrapped in a <response> tag
    { regex: /<response>(.*?)<\/response>/gis, replacement: '$1' },
  ];

  return transforms.reduce(
    (transformed, { regex, replacement }) => transformed.replace(regex, replacement),
    content,
  );
};

const computeMessageProperties = (
  response: MessageContent[],
): {
  thinkingContent: string;
  answerContent: string;
  currentState: MessageState;
} => {
  const thinkingContent = processContentTransforms(
    response
      .filter(r => r.state !== 'ANSWERING')
      .map(r => r.content)
      .join(''),
  );

  const answerContent = processContentTransforms(
    response
      .filter(r => r.state === 'ANSWERING')
      .map(r => r.content)
      .join(''),
  );

  const currentState = response[response.length - 1]?.state || 'SENT';

  return { thinkingContent, answerContent, currentState };
};

const appendToResponse = (
  message: ChatMessage,
  state: MessageState,
  content: string,
): ChatMessage => {
  const transformedContent = processContentTransforms(content);
  const newResponse = [...message.response];

  const lastResponse = newResponse[newResponse.length - 1];
  if (lastResponse && lastResponse.state === state) {
    newResponse[newResponse.length - 1] = {
      ...lastResponse,
      content: lastResponse.content + transformedContent,
    };
  } else {
    newResponse.push({ state, content: transformedContent });
  }

  const { thinkingContent, answerContent, currentState } = computeMessageProperties(newResponse);

  return {
    ...message,
    response: newResponse,
    thinkingContent,
    answerContent,
    currentState,
  };
};

export function useChatMessages(options?: UseChatMessagesOptions) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [latestIncomingId, setLatestIncomingId] = useState<string | null>(null);
  const [currentChatState, setCurrentChatState] = useState<ChatState | null>(null);
  const chatStateRef = useRef<ChatState | null>(null);
  const {
    streamRequest,
    abort: abortTransport,
    buildClientChatUrl,
    buildCrossClientChatUrl,
    buildDocumentEditUrl,
  } = useChatTransport();

  const mode = options?.mode ?? ChatMode.CLIENT;
  const modeConfig = MODE_CONFIGS[mode];
  const onStateChangeRef = useRef(options?.onStateChange);
  onStateChangeRef.current = options?.onStateChange;

  const modeConfigRef = useRef(modeConfig);
  modeConfigRef.current = modeConfig;

  const updateChatState = useCallback((newState: ChatState | null) => {
    chatStateRef.current = newState;
    setCurrentChatState(newState);
  }, []);

  const addMessage = useCallback((message: ChatMessage): void => {
    setMessages(prev => [...prev, message]);
    if (message.direction === MessageDirection.Incoming) {
      setLatestIncomingId(message.id);
    }
  }, []);

  const handleIncomingStreamChunk = useCallback(
    (messageId: string, rawData: string): void => {
      if (chatStateRef.current === null) return;

      const parsedChunk = parseStreamChunk(rawData);
      if (!parsedChunk) return;

      const { state, content, data } = parsedChunk;
      const config = modeConfigRef.current;

      if (state === ChatState.EDITING) {
        if (!config.handledStates.includes(ChatState.EDITING)) return;
        if (chatStateRef.current !== ChatState.EDITING) {
          updateChatState(ChatState.EDITING);
          onStateChangeRef.current?.(ChatState.EDITING);
        }
        return;
      }

      if (state === ChatState.FINAL) {
        if (!config.handledStates.includes(ChatState.FINAL)) return;
        updateChatState(ChatState.FINAL);
        onStateChangeRef.current?.(ChatState.FINAL, data);

        if (data?.assistantMessage) {
          setMessages(currentMessages => {
            const targetMessage = findChatMessageById(currentMessages, messageId);
            if (!targetMessage || targetMessage.answerContent.trim()) return currentMessages;
            const updatedMessage = appendToResponse(
              targetMessage,
              ChatState.ANSWERING,
              data.assistantMessage,
            );
            return currentMessages.map(msg => (msg.id === messageId ? updatedMessage : msg));
          });
          return;
        }

        setMessages(currentMessages => {
          const targetMessage = findChatMessageById(currentMessages, messageId);
          if (!targetMessage || !isEmptyThinkingPlaceholder(targetMessage)) return currentMessages;
          return currentMessages.filter(msg => msg.id !== messageId);
        });
        setLatestIncomingId(prev => (prev === messageId ? null : prev));
        return;
      }

      if (state === ChatState.FILE_PRESENTED) {
        let file: PresentedFile;
        try {
          file = JSON.parse(content) as PresentedFile;
        } catch (e) {
          Sentry.captureException(e);
          return;
        }
        setMessages(currentMessages => {
          const target = findChatMessageById(currentMessages, messageId);
          if (!target) return currentMessages;
          return currentMessages.map(msg =>
            msg.id === messageId
              ? { ...target, presentedFiles: [...(target.presentedFiles ?? []), file] }
              : msg,
          );
        });
        return;
      }

      if (state === ChatState.TOOL_LIMIT_REACHED) {
        setMessages(currentMessages => {
          const target = findChatMessageById(currentMessages, messageId);
          if (!target) return currentMessages;
          return currentMessages.map(msg =>
            msg.id === messageId
              ? {
                  ...target,
                  currentState: ChatState.TOOL_LIMIT_REACHED,
                  currentToolName: undefined,
                }
              : msg,
          );
        });
        return;
      }

      if (state === ChatState.TOOL_STARTED || state === ChatState.TOOL_FINISHED) {
        updateChatState(
          state === ChatState.TOOL_STARTED ? ChatState.THINKING : ChatState.ANSWERING,
        );
        setMessages(currentMessages => {
          const targetMessage = findChatMessageById(currentMessages, messageId);
          if (!targetMessage) return currentMessages;
          const label = state === ChatState.TOOL_STARTED ? `${content}...` : '';
          const updatedMessage = { ...targetMessage, currentToolName: label };
          return currentMessages.map(msg => (msg.id === messageId ? updatedMessage : msg));
        });
        return;
      }

      if (state === ChatState.THINKING && chatStateRef.current !== ChatState.THINKING) {
        updateChatState(ChatState.THINKING);
        if (config.fireTransitionCallbacks) {
          onStateChangeRef.current?.(ChatState.THINKING);
        }
      } else if (state === ChatState.ANSWERING && chatStateRef.current !== ChatState.ANSWERING) {
        updateChatState(ChatState.ANSWERING);
        if (config.fireTransitionCallbacks) {
          onStateChangeRef.current?.(ChatState.ANSWERING);
        }
      }

      setMessages(currentMessages => {
        const targetMessage = findChatMessageById(currentMessages, messageId);
        if (!targetMessage) {
          const newMessage = createResponseMessage();
          newMessage.id = messageId;
          const updatedMessage = appendToResponse(newMessage, state, content);
          setLatestIncomingId(messageId);
          return [...currentMessages, updatedMessage];
        }

        const updatedMessage = appendToResponse(targetMessage, state, content);
        return currentMessages.map(msg => (msg.id === messageId ? updatedMessage : msg));
      });
    },
    [updateChatState],
  );

  const removeEmptyPlaceholderAndReset = useCallback(() => {
    setLatestIncomingId(null);
    setMessages(prev => prev.filter(msg => !isEmptyThinkingPlaceholder(msg)));
    if (modeConfigRef.current.fireTransitionCallbacks) {
      onStateChangeRef.current?.(ChatState.CANCELLED);
    }
    updateChatState(null);
  }, [updateChatState]);

  const fetchAndHandleStreamResponse = useCallback(
    async (url: string, body: object, receivedMessageId: string): Promise<void> => {
      await streamRequest(
        url,
        body,
        rawData => handleIncomingStreamChunk(receivedMessageId, rawData),
        () => {
          removeEmptyPlaceholderAndReset();
          addMessage(
            createResponseMessage(
              'An error occurred while processing your request, please wait a moment and try again. If the issue persists, please contact support.',
            ),
          );
        },
      );

      setMessages(prev => prev.filter(msg => !isEmptyThinkingPlaceholder(msg)));
      updateChatState(null);
    },
    [
      streamRequest,
      handleIncomingStreamChunk,
      updateChatState,
      addMessage,
      removeEmptyPlaceholderAndReset,
    ],
  );

  const sendUserMessage = useCallback(
    async (content: string, clientId: string | null, conversationId: string): Promise<void> => {
      if (chatStateRef.current !== null) {
        Sentry.addBreadcrumb({
          category: 'siesta',
          level: 'warning',
          message: 'sendUserMessage dropped: stream already in flight',
          data: { chatState: chatStateRef.current },
        });
        return;
      }

      updateChatState(ChatState.THINKING);
      if (modeConfigRef.current.fireTransitionCallbacks) {
        onStateChangeRef.current?.(ChatState.THINKING);
      }

      const messageId = uuidv4();
      addMessage(createUserMessage(content));
      addMessage(createThinkingPlaceholderMessage(messageId));

      try {
        if (mode === ChatMode.DOCUMENT) {
          const docOptions = options as DocumentChatOptions;
          const body = docOptions.requestBodyBuilder(content, conversationId);
          await fetchAndHandleStreamResponse(
            buildDocumentEditUrl(docOptions.entityId),
            body,
            messageId,
          );
          return;
        }

        const url = clientId ? buildClientChatUrl(clientId) : buildCrossClientChatUrl();
        await fetchAndHandleStreamResponse(url, { conversationId, message: content }, messageId);
      } catch (error) {
        Sentry.captureException(error);
        removeEmptyPlaceholderAndReset();
      }
    },
    [
      mode,
      options,
      fetchAndHandleStreamResponse,
      addMessage,
      buildClientChatUrl,
      buildCrossClientChatUrl,
      buildDocumentEditUrl,
      updateChatState,
      removeEmptyPlaceholderAndReset,
    ],
  );

  const cancelStreaming = useCallback(() => {
    abortTransport();
    removeEmptyPlaceholderAndReset();
  }, [abortTransport, removeEmptyPlaceholderAndReset]);

  const clearMessages = useCallback(() => {
    cancelStreaming();
    setMessages([]);
  }, [cancelStreaming]);

  const replaceLastAssistantMessage = useCallback((content: string) => {
    setMessages(prev => {
      const lastIncomingIdx = [...prev]
        .reverse()
        .findIndex(m => isChatMessage(m) && m.direction === MessageDirection.Incoming);
      if (lastIncomingIdx === -1) return prev;
      const idx = prev.length - 1 - lastIncomingIdx;
      const replacement = createResponseMessage(content, prev[idx].id);
      return [...prev.slice(0, idx), replacement, ...prev.slice(idx + 1)];
    });
  }, []);

  const appendInterruptedMarker = useCallback(() => {
    setMessages(prev => [...prev, { kind: 'interrupted', id: uuidv4() }]);
  }, []);

  const finaliseLimitReachedMessage = useCallback((id: string) => {
    setMessages(prev =>
      prev.flatMap(msg => {
        if (msg.id !== id || !isChatMessage(msg)) return [msg];
        const hasPrePauseContent = Boolean(msg.answerContent.trim() || msg.thinkingContent.trim());
        if (!hasPrePauseContent) return [];
        return [{ ...msg, currentState: ChatState.ANSWERING }];
      }),
    );
  }, []);

  return {
    messages,
    sendUserMessage,
    latestIncomingId,
    clearMessages,
    replaceLastAssistantMessage,
    appendInterruptedMarker,
    finaliseLimitReachedMessage,
    currentChatState,
    cancelStreaming,
  };
}
